import { isSome } from '@mbfab/shared'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'

import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// mbscan manager-client の同名部品と共通。片側だけで変えない
// 使い方のルール: docs/develop/client_ui/components/page-header.md

// 画面上部の見出し（パンくず・戻る導線 / タイトル / 説明 / 右側のアクション）。通常画面・admin 画面の全メイン画面で共通。
// ポリシーは docs/develop/client_ui/components/page-header.md と同じ:
//   - 見出しサイズは1種類のみ。青字の英字ラベル（eyebrow）は付けない
//   - 説明文（グレー1文）は必須。詳細画面でも「何をする画面か」を1文で書く
//   - 右上の actions は Button size="lg" + 先頭アイコン付きで揃える
//   - 1つ深い階層の画面（一覧から開く詳細・アップロード画面など）は parent を必ず渡す。
//     パンくず（親画面 › この画面）とタイトル左の戻るボタンを同じ遷移先で出す
//     （画面ごとに戻る導線の有無・位置がばらつくと、元の画面へ戻れず迷うため）
//
// 外側の余白・幅（mb-6 / mx-auto max-w-*）は画面ごとに異なるため className で受け取る
export function PageHeader({
  parent,
  title,
  titleTestId,
  badge,
  description,
  actions,
  className,
}: {
  /**
   * 親画面（1つ上の階層）。渡すとパンくずと戻るボタン（aria-label=`${label}へ戻る`）を出す。
   * components/ui はルーターに依存しないため、遷移は呼び出し側の navigate で行う
   */
  parent?: { label: string; onNavigate: () => void }
  title: ReactNode
  titleTestId?: string
  /** タイトル右に並べるステータスタグ等 */
  badge?: ReactNode
  /** 画面の目的を1文で（「〜します。」）。全画面で必須 */
  description: ReactNode
  /**
   * 右上の操作群。Link に buttonVariants を当てるときは、枠線が消えないよう `cn(buttonVariants(...))` を通す。渡すのは `Button size="lg"`（先頭にアイコン）か、`buttonVariants({ size: 'lg' })` を className に当てた `Link`。
   * 主操作（variant 既定）は最大1つで右端に置き、それ以外は outline。手書きの h-* で高さを合わせない
   */
  actions?: ReactNode
  className?: string
}) {
  return (
    <header className={cn('space-y-2', className)}>
      {isSome(parent) && (
        <nav aria-label="パンくずリスト">
          <ol className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
            <li>
              <button
                type="button"
                onClick={parent.onNavigate}
                className="cursor-pointer rounded-sm underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                {parent.label}
              </button>
            </li>
            <li aria-hidden className="flex items-center">
              <ChevronRight className="size-3.5" />
            </li>
            <li aria-current="page" className="font-medium text-foreground">
              {title}
            </li>
          </ol>
        </nav>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-2">
          {isSome(parent) && (
            <button
              type="button"
              aria-label={`${parent.label}へ戻る`}
              onClick={parent.onNavigate}
              className={cn(buttonVariants({ variant: 'ghost', size: 'icon-lg' }), 'shrink-0')}
            >
              <ArrowLeft />
            </button>
          )}
          <div className="min-w-0">
            <div className="flex min-h-9 flex-wrap items-center gap-3">
              <h1 data-testid={titleTestId} className="text-2xl font-black tracking-tight text-foreground">
                {title}
              </h1>
              {badge}
            </div>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{description}</p>
          </div>
        </div>
        {/* actions は `条件 && <Button/>` の形で渡されることがあるため、false のときは枠ごと出さない */}
        {isSome(actions) && actions !== false && (
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">{actions}</div>
        )}
      </div>
    </header>
  )
}
